import { createClient } from '@supabase/supabase-js'
import { logger } from './logger'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Cliente público que NÃO persiste sessão (para o QuizViewer)
export const publicSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false
  }
})

// Função para obter timestamp atual em ISO
// IMPORTANTE: Retorna UTC (Z). O banco (timestamptz) e o frontend (toLocaleString) devem lidar com a conversão.
// Mantemos o nome da função para compatibilidade mas removemos a subtração manual que causava bugs.
export const nowGmtMinus3ISO = (): string => {
  return new Date().toISOString()
}

// Helper para converter timestamp para ISO
export const adjustToGmtMinus3 = (timestamp: string): string => {
  return new Date(timestamp).toISOString()
}

// Função para converter data para ISO string
export const toGmtMinus3ISO = (date: Date): string => {
  return date.toISOString()
}

// Types baseados no schema do banco
type QuizStatus = 'draft' | 'published' | 'archived'

export interface QuizSession {
  id: string
  quiz_id: string
  host: string
  path: string
  status: 'active' | 'completed' | 'abandoned'
  current_page_index: number
  answers: Record<string, any>
  leads: Record<string, any>
  session_data?: {
    page_history?: Array<{
      pageId: string
      pageIndex: number
      viewedAt: string
      completedAt?: string
      timeSpent?: number
    }>
    [key: string]: any
  }
  started_at: string
  completed_at?: string
  total_time_seconds?: number
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  referrer?: string
  user_agent?: string
  created_at: string
  updated_at: string
}

export interface PublishedQuiz {
  id: string
  title: string
  description: string | null
  quiz_data: any
  settings: any
  project_id: string
  slug: string
  status: QuizStatus
  created_at: string
  updated_at: string
}

// RPC Functions
export const supabaseRPC = {
  // Resolver contexto do host (domínio/subdomínio)
  async resolveHostContext(host: string, client = supabase) {
    const { data, error } = await client
      .rpc('resolve_host_context', { p_host: host })

    if (error) throw error
    return data
  },

  // Obter quiz publicado por subdomain do usuário + slug (para domínio principal: quiz.accesso.pro/{subdomain}/{slug})
  async getQuizByUserSubdomain(userSubdomain: string, slug: string, client = supabase) {
    const { data, error } = await client
      .rpc('get_quiz_by_user_subdomain', {
        p_user_subdomain: userSubdomain,
        p_slug: slug
      })

    if (error) throw error
    return data as PublishedQuiz | null
  },

  // Obter quiz publicado por host + slug (para domínios personalizados: meudominio.com/{slug})
  async getPublishedQuiz(host: string, slug: string, client = supabase) {
    const { data, error } = await client
      .rpc('get_published_quiz', {
        p_host: host,
        p_slug: slug
      })

    if (error) throw error
    return data as PublishedQuiz | null
  },

  // Criar sessão de quiz
  async createQuizSession(quizId: string, initialData?: any, client = supabase) {
    const { data, error } = await client
      .rpc('create_quiz_session', {
        p_quiz_id: quizId,
        p_initial_data: initialData || {}
      })

    if (error) throw error
    return data as QuizSession
  },

  // Atualizar sessão de quiz
  async updateQuizSession(sessionId: string, sessionData: any, client = supabase) {
    const { data, error } = await client
      .rpc('update_quiz_session', {
        p_session_id: sessionId,
        p_session_data: sessionData
      })

    if (error) throw error
    return data as QuizSession
  },

  // Atualizar progresso da sessão
  async updateSessionProgress(sessionId: string, params: {
    currentPageIndex?: number
    completed?: boolean
    timeSpent?: number
  }, client = supabase) {
    const { error } = await client
      .rpc('append_session_payload', {
        p_session_id: sessionId,
        p_current_page_index: params.currentPageIndex || null,
        p_answers: null,
        p_leads: null,
        p_completed: params.completed || false,
        p_time_spent: params.timeSpent || null
      })

    if (error) throw error
  },

  // Obter sessões por quiz (para dashboard)
  async getQuizSessions(quizId: string) {
    const { data, error } = await supabase
      .from('quiz_sessions')
      .select('*')
      .eq('quiz_id', quizId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data as QuizSession[]
  },

  // Alternar status de publicação do quiz
  async toggleQuizStatus(quizId: string, newStatus: QuizStatus) {
    const { data, error } = await supabase
      .from('quizzes')
      .update({ status: newStatus, updated_at: nowGmtMinus3ISO() })
      .eq('id', quizId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Obter ID do usuário atual (temporário para testes)
  async getCurrentUserId() {
    const { data: { user } } = await supabase.auth.getUser()
    return user?.id || null
  },

  // CRUD para quizzes
  async createQuiz(data: {
    project_id: string
    user_id: string
    name: string
    slug?: string
    data: any
    status?: 'draft' | 'published'
  }) {
    const { data: newQuiz, error } = await supabase
      .from('quizzes')
      .insert([{
        ...data,
        status: data.status || 'draft',
        created_at: nowGmtMinus3ISO(),
        updated_at: nowGmtMinus3ISO()
      }])
      .select()
      .single()

    if (error) throw error
    return newQuiz
  },

  async updateQuiz(id: string, data: Partial<any>) {
    const { data: updatedQuiz, error } = await supabase
      .from('quizzes')
      .update({
        ...data,
        updated_at: nowGmtMinus3ISO()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return updatedQuiz
  },

  async deleteQuiz(id: string) {
    const { error } = await supabase
      .from('quizzes')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async getQuizzesByProject(projectId: string) {
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  async getQuizByShareId(shareId: string) {
    // Tenta via RPC primeiro (para contornar RLS se a função existir)
    try {
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_quiz_by_share_id', { p_share_id: shareId })

      // Se a RPC funcionar e retornar dados (array ou objeto)
      if (!rpcError && rpcData) {
        // Se retornar array (SETOF), pega o primeiro
        if (Array.isArray(rpcData) && rpcData.length > 0) return rpcData[0];
        // Se retornar objeto único
        if (!Array.isArray(rpcData)) return rpcData;
      }
    } catch (e) {
      // Ignora erro de RPC inexistente e tenta query direta
    }

    // Fallback: Query direta
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('share_id', shareId)
      .single()

    if (error) return null
    return data
  },

  async setGeminiApiKey(key: string | null) {
    const { data, error } = await supabase
      .rpc('set_gemini_api_key', { p_key: key })

    if (error) throw error
    return data
  }
}

// Helper para extrair host do window.location
export function getCurrentHost(): string {
  return window.location.host // retorna 'dominio.com' ou 'subdominio.dominio.com'
}

// Helper para extrair subdomain do usuário da URL (primeiro segmento)
export function getUserSubdomainFromPath(): string {
  const path = window.location.pathname
  const parts = path.split('/').filter(Boolean)
  // URL: /{subdomain}/{slug} - primeiro segmento é o subdomain
  return parts.length >= 2 ? parts[0] : ''
}

// Helper para extrair slug da URL (segundo segmento ou único segmento)
export function getSlugFromPath(): string {
  const path = window.location.pathname
  const parts = path.split('/').filter(Boolean)
  // URL: /{subdomain}/{slug} -> retorna o slug (segundo segmento)
  // URL: /{slug} (domínio personalizado) -> retorna o slug (primeiro segmento)
  if (parts.length >= 2) {
    return parts[1] // Formato: /{subdomain}/{slug}
  }
  return parts[0] || '' // Formato antigo ou domínio personalizado: /{slug}
}

// Helper para extrair parâmetros UTM da URL
export function getUTMParameters(): {
  source?: string
  medium?: string
  campaign?: string
  term?: string
  content?: string
} {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    source: urlParams.get('utm_source') || undefined,
    medium: urlParams.get('utm_medium') || undefined,
    campaign: urlParams.get('utm_campaign') || undefined,
    term: urlParams.get('utm_term') || undefined,
    content: urlParams.get('utm_content') || undefined
  }
}

// Helper para obter referrer
export function getReferrer(): string | undefined {
  return document.referrer || undefined
}

// Helper para obter cookie
function getCookie(name: string): string | null {
  try {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift()
      return cookieValue ? decodeURIComponent(cookieValue) : null
    }
  } catch {
    return null
  }
  return null
}

// Helper para obter valor do localStorage
function getLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

// Cache para armazenar os parâmetros do Facebook capturados
let cachedFacebookParams: {
  fbp?: string
  fbc?: string
  fbclid?: string
} | null = null

// Helper para extrair parâmetros do Facebook (fbp, fbc, fbclid)
// Esta função tenta capturar os cookies que podem ser criados assincronamente pelo Pixel
function readFacebookCookies(): {
  fbp?: string
  fbc?: string
  fbclid?: string
} {
  const urlParams = new URLSearchParams(window.location.search)

  // fbclid vem da URL
  const fbclid = urlParams.get('fbclid') || undefined

  // fbp e fbc vêm de cookies ou localStorage
  const fbp = getCookie('_fbp') || getLocalStorage('_fbp') || undefined
  const fbc = getCookie('_fbc') || getLocalStorage('_fbc') || undefined

  return {
    fbp,
    fbc,
    fbclid
  }
}

// Função para atualizar o cache com os parâmetros mais recentes
function updateFacebookParamsCache(): void {
  const params = readFacebookCookies()

  // Só atualiza o cache se encontrou algum parâmetro
  if (params.fbp || params.fbc || params.fbclid) {
    cachedFacebookParams = params

    // Debug log
    console.log('[FB Params] Capturados:', {
      fbp: params.fbp ? '✓' : '✗',
      fbc: params.fbc ? '✓' : '✗',
      fbclid: params.fbclid ? '✓' : '✗'
    })
  }
}

// Inicializa o polling para capturar cookies do Facebook Pixel
// O Pixel do Facebook cria os cookies de forma assíncrona, então precisamos tentar várias vezes
function initFacebookParamsPolling(): void {
  // Primeira tentativa imediata
  updateFacebookParamsCache()

  // Retries após o load (quando o Pixel provavelmente já criou os cookies)
  // Estratégia: 600ms, 1500ms, 3000ms
  const retries = [600, 1500, 3000]

  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      updateFacebookParamsCache()
      retries.forEach(ms => {
        setTimeout(() => updateFacebookParamsCache(), ms)
      })
    })
  }
}

// Inicializa o polling quando o módulo é carregado
if (typeof window !== 'undefined') {
  initFacebookParamsPolling()
}

// Função pública para obter os parâmetros do Facebook
export function getFacebookParameters(): {
  fbp?: string
  fbc?: string
  fbclid?: string
} {
  // Tenta capturar novamente (caso ainda não tenha cache)
  if (!cachedFacebookParams) {
    updateFacebookParamsCache()
  }

  // Retorna o cache ou tenta ler diretamente
  return cachedFacebookParams || readFacebookCookies()
}

// Helper para adicionar parâmetros do Facebook a uma URL
export function addFacebookParamsToUrl(url: string, fbParams?: {
  fbp?: string
  fbc?: string
  fbclid?: string
}): string {
  if (!fbParams) {
    // Se não foram passados parâmetros, tentar capturar
    fbParams = getFacebookParameters()
  }

  // Se não há parâmetros do Facebook, retornar URL original
  const hasFBParams = fbParams.fbp || fbParams.fbc || fbParams.fbclid
  if (!hasFBParams) {
    return url
  }

  // Verificar se a URL é absoluta
  const isAbsoluteUrl = url.startsWith('http://') || url.startsWith('https://')

  try {
    if (isAbsoluteUrl) {
      const urlObj = new URL(url)

      // Adicionar parâmetros apenas se não existirem
      if (fbParams.fbp && !urlObj.searchParams.has('fbp')) {
        urlObj.searchParams.set('fbp', fbParams.fbp)
      }
      if (fbParams.fbc && !urlObj.searchParams.has('fbc')) {
        urlObj.searchParams.set('fbc', fbParams.fbc)
      }
      if (fbParams.fbclid && !urlObj.searchParams.has('fbclid')) {
        urlObj.searchParams.set('fbclid', fbParams.fbclid)
      }

      return urlObj.toString()
    } else {
      // URL relativa
      const fbParts: string[] = []

      if (fbParams.fbp && !url.includes('fbp=')) {
        fbParts.push(`fbp=${encodeURIComponent(fbParams.fbp)}`)
      }
      if (fbParams.fbc && !url.includes('fbc=')) {
        fbParts.push(`fbc=${encodeURIComponent(fbParams.fbc)}`)
      }
      if (fbParams.fbclid && !url.includes('fbclid=')) {
        fbParts.push(`fbclid=${encodeURIComponent(fbParams.fbclid)}`)
      }

      if (fbParts.length > 0) {
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}${fbParts.join('&')}`
      }

      return url
    }
  } catch (err) {
    logger.warn('⚠️ [addFacebookParamsToUrl] Erro ao processar URL, usando fallback:', err)

    // Fallback manual
    const fbParts: string[] = []

    if (fbParams.fbp && !url.includes('fbp=')) {
      fbParts.push(`fbp=${encodeURIComponent(fbParams.fbp)}`)
    }
    if (fbParams.fbc && !url.includes('fbc=')) {
      fbParts.push(`fbc=${encodeURIComponent(fbParams.fbc)}`)
    }
    if (fbParams.fbclid && !url.includes('fbclid=')) {
      fbParts.push(`fbclid=${encodeURIComponent(fbParams.fbclid)}`)
    }

    if (fbParts.length > 0) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}${fbParts.join('&')}`
    }

    return url
  }
}

// Helper para adicionar UTMs a uma URL
// Função auxiliar para verificar se um parâmetro UTM já existe na URL
function hasUTMParameter(url: string, paramName: string): boolean {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://dummy.com${url}`)
    return urlObj.searchParams.has(paramName)
  } catch {
    // Se não conseguir parsear a URL, verificar manualmente
    const queryIndex = url.indexOf('?')
    if (queryIndex === -1) return false

    const queryString = url.substring(queryIndex + 1)
    const params = new URLSearchParams(queryString)
    return params.has(paramName)
  }
}

export function addUTMsToUrl(url: string, utmParams?: {
  source?: string
  medium?: string
  campaign?: string
  term?: string
  content?: string
}): string {
  if (!utmParams) {
    // Se não foram passadas UTMs, tentar capturar da URL atual
    utmParams = getUTMParameters()
  }

  // Se não há UTMs, retornar URL original
  const hasUTMs = utmParams.source || utmParams.medium || utmParams.campaign || utmParams.term || utmParams.content
  if (!hasUTMs) {
    return url
  }

  // Verificar se a URL é absoluta (começa com http:// ou https://)
  const isAbsoluteUrl = url.startsWith('http://') || url.startsWith('https://')

  try {
    // Se for URL absoluta, usar URL constructor
    if (isAbsoluteUrl) {
      const urlObj = new URL(url)

      // Adicionar UTMs apenas se não existirem na URL de destino
      if (utmParams.source && !urlObj.searchParams.has('utm_source')) {
        urlObj.searchParams.set('utm_source', utmParams.source)
      }
      if (utmParams.medium && !urlObj.searchParams.has('utm_medium')) {
        urlObj.searchParams.set('utm_medium', utmParams.medium)
      }
      if (utmParams.campaign && !urlObj.searchParams.has('utm_campaign')) {
        urlObj.searchParams.set('utm_campaign', utmParams.campaign)
      }
      if (utmParams.term && !urlObj.searchParams.has('utm_term')) {
        urlObj.searchParams.set('utm_term', utmParams.term)
      }
      if (utmParams.content && !urlObj.searchParams.has('utm_content')) {
        urlObj.searchParams.set('utm_content', utmParams.content)
      }

      const srcValue = `${utmParams.source || ''}|${utmParams.campaign || ''}|${utmParams.medium || ''}|${utmParams.content || ''}|${utmParams.term || ''}`
      if (!urlObj.searchParams.has('src')) {
        urlObj.searchParams.set('src', srcValue)
      }

      return urlObj.toString()
    } else {
      // URL relativa, adicionar UTMs manualmente apenas se não existirem
      const utmParts: string[] = []

      if (utmParams.source && !hasUTMParameter(url, 'utm_source')) {
        utmParts.push(`utm_source=${encodeURIComponent(utmParams.source)}`)
      }
      if (utmParams.medium && !hasUTMParameter(url, 'utm_medium')) {
        utmParts.push(`utm_medium=${encodeURIComponent(utmParams.medium)}`)
      }
      if (utmParams.campaign && !hasUTMParameter(url, 'utm_campaign')) {
        utmParts.push(`utm_campaign=${encodeURIComponent(utmParams.campaign)}`)
      }
      if (utmParams.term && !hasUTMParameter(url, 'utm_term')) {
        utmParts.push(`utm_term=${encodeURIComponent(utmParams.term)}`)
      }
      if (utmParams.content && !hasUTMParameter(url, 'utm_content')) {
        utmParts.push(`utm_content=${encodeURIComponent(utmParams.content)}`)
      }

      const srcValue = `${utmParams.source || ''}|${utmParams.campaign || ''}|${utmParams.medium || ''}|${utmParams.content || ''}|${utmParams.term || ''}`
      if (!hasUTMParameter(url, 'src')) {
        utmParts.push(`src=${encodeURIComponent(srcValue)}`)
      }

      if (utmParts.length > 0) {
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}${utmParts.join('&')}`
      }

      return url
    }
  } catch (err) {
    // Fallback: se houver erro, adicionar UTMs manualmente apenas se não existirem
    logger.warn('⚠️ [addUTMsToUrl] Erro ao processar URL, usando fallback:', err)

    const utmParts: string[] = []

    if (utmParams.source && !hasUTMParameter(url, 'utm_source')) {
      utmParts.push(`utm_source=${encodeURIComponent(utmParams.source)}`)
    }
    if (utmParams.medium && !hasUTMParameter(url, 'utm_medium')) {
      utmParts.push(`utm_medium=${encodeURIComponent(utmParams.medium)}`)
    }
    if (utmParams.campaign && !hasUTMParameter(url, 'utm_campaign')) {
      utmParts.push(`utm_campaign=${encodeURIComponent(utmParams.campaign)}`)
    }
    if (utmParams.term && !hasUTMParameter(url, 'utm_term')) {
      utmParts.push(`utm_term=${encodeURIComponent(utmParams.term)}`)
    }
    if (utmParams.content && !hasUTMParameter(url, 'utm_content')) {
      utmParts.push(`utm_content=${encodeURIComponent(utmParams.content)}`)
    }

    const srcValue = `${utmParams.source || ''}|${utmParams.campaign || ''}|${utmParams.medium || ''}|${utmParams.content || ''}|${utmParams.term || ''}`
    if (!hasUTMParameter(url, 'src')) {
      utmParts.push(`src=${encodeURIComponent(srcValue)}`)
    }

    if (utmParts.length > 0) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}${utmParts.join('&')}`
    }

    return url
  }
}

// Helper para adicionar UTMs E parâmetros do Facebook a uma URL (função combinada)
export function addTrackingParamsToUrl(url: string, params?: {
  utm?: {
    source?: string
    medium?: string
    campaign?: string
    term?: string
    content?: string
  }
  facebook?: {
    fbp?: string
    fbc?: string
    fbclid?: string
  }
}): string {
  // Primeiro adiciona UTMs
  let urlWithUTMs = addUTMsToUrl(url, params?.utm)

  // Depois adiciona parâmetros do Facebook
  let urlWithTracking = addFacebookParamsToUrl(urlWithUTMs, params?.facebook)

  return urlWithTracking
}

// Helper para gerar ID único (pode ser usado para outros propósitos)
export function generateUniqueId(): string {
  return crypto.randomUUID()
}

// Lista de user agents conhecidos de robôs/crawlers
const BOT_USER_AGENTS = [
  'googlebot',
  'bingbot',
  'slurp',
  'duckduckbot',
  'baiduspider',
  'yandexbot',
  'sogou',
  'exabot',
  'facebot',
  'facebookexternalhit',
  'twitterbot',
  'linkedinbot',

  'telegrambot',
  'discordbot',
  'slackbot',
  'applebot',
  'samsungbot',
  'ahrefsbot',
  'mj12bot',
  'dotbot',
  'semrushbot',
  'rogerbot',
  'archive.org_bot',
  'ia_archiver',
  'special_archiver',
  'crawler',
  'spider',
  'bot',
  'crawler',
  'scraper'
]

// Helper para detectar se o user agent é de um robô
export function isBotUserAgent(userAgent: string): boolean {
  if (!userAgent) return false

  const ua = userAgent.toLowerCase()
  return BOT_USER_AGENTS.some(botName => ua.includes(botName))
}

/**
 * Detecta se o acesso é um prefetch do Meta Ads (Facebook/Instagram)
 * 
 * O Meta Ads faz "prefetch" das landing pages quando o usuário VÊ um anúncio
 * (antes de clicar) para acelerar o carregamento. Isso usa user agents genéricos
 * que parecem navegadores reais, mas não são usuários reais.
 * 
 * Padrões identificados:
 * 1. Android com "Android 10; K" (genérico, sem modelo de dispositivo)
 *    - Dispositivos reais têm modelos como SM-A325M, Pixel 7, Redmi Note, etc.
 * 2. iPhone com Safari genérico, SEM identificadores de app (Instagram/Facebook)
 *    - Usuários reais vindos de anúncios têm IABMV (Instagram) ou FBAN/FBAV (Facebook)
 * 3. Windows desktop com referrer mobile do Meta (inconsistência)
 *    - Usuário real em desktop não teria referrer de m.facebook.com
 * 4. iPhone com FBAN mas sem Safari/ no final (verificação interna do Facebook)
 *    - Formato: "Mobile/xxxxx [FBAN/...]" sem Safari/xxx
 * 
 * @param userAgent - O user agent do navegador
 * @returns true se for provável prefetch do Meta
 */
export function isMetaPrefetch(userAgent: string): boolean {
  if (!userAgent) return false

  const ua = userAgent
  const referrer = document.referrer.toLowerCase()

  // Variáveis auxiliares usadas em múltiplos padrões
  const hasInstagramApp = ua.toLowerCase().includes('instagram')
  const hasFacebookApp = ua.includes('FBAN') || ua.includes('FBAV') || ua.includes('FB_IAB')
  const isFromMeta = referrer.includes('facebook.com') || referrer.includes('instagram.com') || referrer.includes('fb.com')

  // Padrão 1: Android genérico "Android 10; K" (sem modelo específico)
  // O "K" é usado pelo Chrome quando não há informação real do dispositivo
  // Importante: NÃO filtrar se vier de app Instagram/Facebook (são usuários reais)
  const isGenericAndroid = ua.includes('Android 10; K') || ua.includes('Android 10;K')

  if (isGenericAndroid && !hasInstagramApp && !hasFacebookApp) {
    return true
  }

  // Padrão 2: iPhone com Safari genérico, sem identificador de app
  // User agents de prefetch: "iPhone...Safari/604.1" (genérico)
  // User agents reais: têm "Instagram" ou "FBAN/FBAV" no UA
  const isIPhone = ua.includes('iPhone')
  const isGenericSafari = ua.includes('Safari/604.1') && !ua.includes('CriOS') // Safari sem Chrome iOS
  const hasNoAppIdentifier = !hasInstagramApp && !hasFacebookApp

  if (isIPhone && isGenericSafari && hasNoAppIdentifier && isFromMeta) {
    return true
  }

  // Padrão 3: Windows desktop com referrer mobile do Meta
  // Usuário real em desktop não teria referrer de m.facebook.com (versão mobile)
  const isWindows = ua.includes('Windows')
  const isFromMetaMobile = referrer.includes('m.facebook.com')

  if (isWindows && isFromMetaMobile) {
    return true
  }

  // Padrão 4: iPhone com FBAN mas sem Safari/ no final
  // Verificação interna do Facebook usa formato: "Mobile/xxxxx [FBAN/...]"
  // User agents reais terminam com "Safari/xxx"
  const hasFBAN = ua.includes('FBAN')
  const hasSafariSuffix = ua.includes('Safari/')

  if (isIPhone && hasFBAN && !hasSafariSuffix) {
    return true
  }

  return false
}

// Helper para detectar device type
export function getDeviceType(): string {
  const ua = navigator.userAgent
  const width = window.innerWidth

  // Mobile detection
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    if (/iPad/i.test(ua) || (width >= 768 && width <= 1024)) {
      return 'tablet'
    }
    return 'mobile'
  }

  // Desktop
  return 'desktop'
}

// Helper para detectar browser
export function getBrowser(): string {
  const ua = navigator.userAgent

  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'chrome'
  if (ua.includes('Firefox')) return 'firefox'
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari'
  if (ua.includes('Edg')) return 'edge'
  if (ua.includes('Opera') || ua.includes('OPR')) return 'opera'
  if (ua.includes('Trident') || ua.includes('MSIE')) return 'ie'

  return 'unknown'
}

// Helper para detectar OS
export function getOS(): string {
  const ua = navigator.userAgent

  if (ua.includes('Windows')) return 'windows'
  if (ua.includes('Mac OS X') || ua.includes('Macintosh')) return 'macos'
  if (ua.includes('Linux')) return 'linux'
  if (ua.includes('Android')) return 'android'
  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) return 'ios'

  return 'unknown'
}

// Helper para obter IP do visitante com timeout
export async function getVisitorIP(): Promise<string | undefined> {
  try {
    // Tentar múltiplos serviços com timeout (apenas HTTPS)
    const services = [
      'https://api.ipify.org?format=json',
      'https://api64.ipify.org?format=json',
      'https://ipwho.is/' // Retorna JSON com IP
    ]

    for (const service of services) {
      try {
        // Criar controller para timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 2000) // 2 segundos timeout

        const response = await fetch(service, {
          signal: controller.signal,
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        })
        clearTimeout(timeoutId)

        if (!response.ok) continue

        const data = await response.json()
        const ip = data.ip || data.query // ipify usa 'ip', ipwho.is usa 'ip'

        if (ip && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip)) {
          return ip
        }
      } catch (e) {
        // Timeout ou erro - tentar próximo serviço
        continue
      }
    }

    return undefined
  } catch (error) {
    return undefined
  }
}

// Helper para obter geolocalização por IP (com múltiplas APIs e fallback)
export async function getGeoLocation(): Promise<{
  country?: string
  region?: string
  city?: string
}> {
  const apis = [
    {
      // ipwho.is: Gratuito, HTTPS, CORS enabled, sem chave de API
      url: 'https://ipwho.is/',
      parse: (data: any) => ({
        country: data.country,
        region: data.region,
        city: data.city
      })
    },
    {
      // ipapi.co: Gratuito (limite de rate), HTTPS
      url: 'https://ipapi.co/json/',
      parse: (data: any) => ({
        country: data.country_name,
        region: data.region,
        city: data.city
      })
    },
    {
      // api.db-ip.com: Alternativa HTTPS gratuita
      url: 'https://api.db-ip.com/v2/free/self',
      parse: (data: any) => ({
        country: data.countryName,
        region: data.stateProv,
        city: data.city
      })
    }
  ]

  for (const api of apis) {
    try {
      // Criar controller para timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 3000) // 3 segundos timeout

      const response = await fetch(api.url, {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })
      clearTimeout(timeoutId)

      if (!response.ok) continue

      const data = await response.json()

      // Verificar se a resposta tem dados válidos (ipwho.is retorna success: false se falhar)
      if (data && (data.success !== false)) {
        const result = api.parse(data)
        // Só retornar se tiver pelo menos o país
        if (result.country) {
          return result
        }
      }
    } catch (error) {
      // Timeout ou erro - tentar próxima API
      continue
    }
  }

  return {}
}

// Helper para obter ID único de tracking de uma página
export function getPageTrackingId(page: { uniqueId?: string; id?: number }): string {
  // Prioriza uniqueId (obrigatório em novos quizzes)
  if (page.uniqueId) {
    return page.uniqueId
  }

  // Fallback para ID sequencial (compatibilidade com quizzes antigos)
  return page.id?.toString() || 'unknown'
}

// Helper para encontrar o ID customizado da pergunta na página
export function getQuestionId(pageData: any): string {
  // Primeiro, procurar por elementos Options com reportId
  if (pageData.elements) {
    for (const element of pageData.elements) {
      if (element.type === 'Options' && element.reportId) {
        return element.reportId
      }
    }

    // Se não encontrou Options, procurar qualquer elemento com reportId
    for (const element of pageData.elements) {
      if (element.reportId) {
        return element.reportId
      }
    }
  }

  // Fallback: usar o ID sequencial da página (compatibilidade)
  return pageData.id?.toString() || 'unknown'
}

// Helper para encontrar IDs customizados de campos de formulário na página
export function getFormFieldIds(pageData: any): Record<string, string> {
  const fieldIds: Record<string, string> = {}

  logger.log('🔍 [getFormFieldIds] Processando página:', pageData?.id, 'elements:', pageData?.elements?.length)

  if (pageData.elements) {
    for (const element of pageData.elements) {
      logger.log('🔍 [getFormFieldIds] Elemento:', { type: element.type, id: element.id, fieldId: (element as any).fieldId })
      // Form é ElementType.Form = 21 (enum numérico, contando do 0)
      if (element.type === 21 || element.type === 'Form') {
        const elementId = element.id?.toString() || 'unknown'
        const customId = element.reportId || (element as any).fieldId || elementId

        logger.log('🔍 [getFormFieldIds] Form encontrado:', { elementId, customId, reportId: element.reportId, fieldId: (element as any).fieldId })

        // Mapear tanto o ID do elemento quanto o fieldId (se existir)
        fieldIds[elementId] = customId
        if ((element as any).fieldId && (element as any).fieldId !== elementId) {
          fieldIds[(element as any).fieldId] = customId
        }
      }
    }
  }

  logger.log('🔍 [getFormFieldIds] fieldIds retornados:', fieldIds)
  return fieldIds
}

// Helper para detectar robôs baseado em múltiplos critérios
export function shouldSkipSession(): boolean {
  const userAgent = navigator.userAgent

  // Verificar user agent de bots conhecidos
  if (isBotUserAgent(userAgent)) {
    console.log('🤖 [shouldSkipSession] Bot detectado via user agent:', userAgent.substring(0, 100))
    return true
  }

  // Verificar se é prefetch do Meta Ads (Facebook/Instagram)
  if (isMetaPrefetch(userAgent)) {
    console.log('🔄 [shouldSkipSession] Prefetch do Meta detectado:', {
      userAgent: userAgent.substring(0, 100),
      referrer: document.referrer
    })
    return true
  }

  // Verificar se é headless browser
  if (navigator.webdriver) {
    console.log('🤖 [shouldSkipSession] Headless browser detectado')
    return true
  }

  // Verificar se não há cookies habilitados (muitos bots não suportam)
  try {
    document.cookie = 'test=1'
    if (document.cookie.indexOf('test') === -1) {
      console.log('🤖 [shouldSkipSession] Cookies desabilitados - possível bot')
      return true
    }
    document.cookie = 'test=; expires=Thu, 01 Jan 1970 00:00:00 GMT'
  } catch (e) {
    console.log('🤖 [shouldSkipSession] Erro ao testar cookies - possível bot')
    return true
  }

  return false
}
